import { aggregateQi, missingQi } from './aggregate';
import { QI_GROUPS, type QiGroup } from './manifest';
import type {
  QiAggregationInput,
  QiBaseline,
  QiCollectionMetadata,
  QiGroupInput,
  QiProvenance,
  QiSubject,
  QiValue,
} from './types';
import { validateQiValues } from './validate';

export const QI_LEDGER_ENGINE_VERSION = 'QI_LEDGER_V1';
export interface QiLedgerPreview {
  restricted: boolean;
  inputs_hash: string | null;
  restricted_group_ids?: string[];
  period: { period_start: string; period_end: string };
  home_profile?: {
    service_identifier: string;
    programme: 'RESIDENTIAL';
    row_version: number;
  } | null;
  groups?: {
    id: string;
    group_code: QiGroup;
    mode: 'SUBJECT' | 'AGGREGATE';
    status: 'NOT_COLLECTED' | 'COLLECTING' | 'COMPLETE';
    collection_metadata: Record<string, string>;
    restricted?: boolean;
  }[];
  subjects?: {
    group_id: string;
    subject_id: string;
    subgroup: string;
    discipline?: string;
    mode?: string;
    criterion: unknown;
    primary_exclusion_reason?: string | null;
    comment?: string | null;
    provenance: QiProvenance;
  }[];
  aggregates?: { group_id: string; code: string; value: QiValue; provenance: QiProvenance }[];
  baselines?: {
    subject_id: string;
    subgroup: string;
    previous_finishing_weight?: number | string | null;
    previous_adl_total?: number | null;
    previous_staff_hours?: number | string | null;
    provenance: QiProvenance;
  }[];
}
/** Wire adapter for qi_ledger_preview_inputs_controlled. Run only in trusted app-server
 * code before service-only attestation; browser values/issues must never be trusted.
 * The hash is supplied by PostgreSQL; do not hash JSON.stringify in its place.
 */
export function aggregateQiPreview(preview: QiLedgerPreview) {
  if (
    preview.restricted ||
    (preview.restricted_group_ids?.length ?? 0) > 0 ||
    preview.groups?.some((g) => g.restricted === true) ||
    !preview.inputs_hash ||
    !preview.groups ||
    !preview.subjects ||
    !preview.aggregates ||
    !preview.baselines
  )
    throw new Error('qi_preview_restricted_or_incomplete');
  const groups: QiAggregationInput['groups'] = {};
  for (const g of preview.groups) {
    if (!QI_GROUPS.includes(g.group_code)) throw new Error('qi_preview_group_invalid');
    const m = g.collection_metadata;
    const metadata: QiCollectionMetadata = {
      collectionDate: m.collection_date,
      windowStart: m.window_start,
      windowEnd: m.window_end,
      reviewerUserId: m.reviewer_user_id,
      polypharmacyDate: m.polypharmacy_date,
      antipsychoticDate: m.antipsychotic_date,
    };
    let input: QiGroupInput;
    if (g.mode === 'SUBJECT')
      input = {
        mode: 'SUBJECT',
        collected: g.status === 'COMPLETE',
        metadata,
        subjects: preview.subjects
          .filter((s) => s.group_id === g.id)
          .map(
            (s) =>
              ({
                group: g.group_code,
                subjectId: s.subject_id,
                subgroup: s.subgroup,
                discipline: s.discipline || undefined,
                mode: s.mode || undefined,
                criterion: s.criterion,
                primaryExclusionReason: s.primary_exclusion_reason ?? undefined,
                comment: s.comment ?? undefined,
                provenance: s.provenance,
              }) as QiSubject,
          ),
      };
    else
      input = {
        mode: 'AGGREGATE',
        metadata,
        values: Object.fromEntries(
          preview.aggregates
            .filter((a) => a.group_id === g.id)
            .map((a) => [
              a.code,
              a.provenance.conformance === 'non_conforming'
                ? missingQi('non_conforming_source')
                : a.value,
            ]),
        ),
      };
    groups[g.group_code] = input;
  }
  const num = (v: number | string | null | undefined) => (v == null ? null : Number(v));
  const baselines: QiBaseline[] = preview.baselines.map((b) => ({
    subjectId: b.subject_id,
    subgroup: b.subgroup,
    previousFinishingWeight: num(b.previous_finishing_weight),
    previousAdlTotal: b.previous_adl_total,
    previousStaffHours: num(b.previous_staff_hours),
    provenance: b.provenance,
  }));
  const result = aggregateQi({
    programme: 'RESIDENTIAL',
    periodStart: preview.period.period_start,
    periodEnd: preview.period.period_end,
    groups,
    baselines,
  });
  const issues = [...result.issues, ...validateQiValues(result.values, true)];
  // Aggregate/baseline provenance participates even when subject-mode calculations do not see it.
  for (const p of [
    ...preview.aggregates
      .filter((a) => preview.groups!.some((g) => g.id === a.group_id && g.mode === 'AGGREGATE'))
      .map((a) => a.provenance),
    ...preview.baselines
      .filter((b) => {
        const group: QiGroup | undefined = ['service_manager', 'rn', 'en', 'pcw'].includes(
          b.subgroup,
        )
          ? 'WF'
          : ['significant', 'consecutive'].includes(b.subgroup)
            ? 'UPWL'
            : b.subgroup === 'adl'
              ? 'ADL'
              : undefined;
        return group && groups[group]?.mode === 'SUBJECT';
      })
      .map((b) => b.provenance),
  ]) {
    if (['prefill', 'import'].includes(p.kind) && p.conformance === 'non_conforming')
      issues.push({
        code: 'non_conforming_value_withheld',
        path: 'provenance',
        category: 'POLICY',
      });
    if (
      ['prefill', 'import'].includes(p.kind) &&
      p.conformance === 'unknown' &&
      (!p.confirmed_by || !p.confirmed_at || !p.override_reason?.trim())
    )
      issues.push({
        code: 'unknown_source_attestation_reason_required',
        path: 'provenance',
        category: 'POLICY',
      });
    if (
      ['prefill', 'import'].includes(p.kind) &&
      p.conformance === 'conforming' &&
      (!p.confirmed_by || !p.confirmed_at) &&
      !p.override_reason?.trim()
    )
      issues.push({
        code: 'prefill_confirmation_override_required',
        path: 'provenance',
        category: 'POLICY',
      });
  }
  return {
    engine_version: QI_LEDGER_ENGINE_VERSION,
    inputs_hash: preview.inputs_hash,
    manifest_values: result.values,
    issues,
  };
}
